import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

function Login({ onShowHome }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  
  // Password strength indicators
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    symbol: false
  });

  // Rate limiting states
  const [attemptCount, setAttemptCount] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimeRemaining, setBlockTimeRemaining] = useState(0);

  // Auto-fill location state
  const [location, setLocation] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);

  useEffect(() => {
    // Check for existing rate limit block
    const checkRateLimit = () => {
      const stored = localStorage.getItem('loginAttempts');
      if (stored) {
        const data = JSON.parse(stored);
        const now = Date.now();
        
        if (data.blockedUntil && now < data.blockedUntil) {
          setIsBlocked(true);
          setBlockTimeRemaining(Math.ceil((data.blockedUntil - now) / 1000));
        } else if (data.blockedUntil && now >= data.blockedUntil) {
          // Block expired, reset
          localStorage.removeItem('loginAttempts');
        } else {
          setAttemptCount(data.count || 0);
        }
      }
    };

    checkRateLimit();

    // Auto-get location if signup
    if (!isLogin) {
      getCurrentLocation();
    }
  }, [isLogin]);

  // Countdown timer for rate limit
  useEffect(() => {
    let timer;
    if (isBlocked && blockTimeRemaining > 0) {
      timer = setInterval(() => {
        setBlockTimeRemaining(prev => {
          if (prev <= 1) {
            setIsBlocked(false);
            localStorage.removeItem('loginAttempts');
            setAttemptCount(0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isBlocked, blockTimeRemaining]);

  // Get user's current location
  const getCurrentLocation = () => {
    setLocationLoading(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            
            // Use reverse geocoding to get city name
            const response = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
            );
            const data = await response.json();
            
            const city = data.city || data.locality || data.principalSubdivision || 'Unknown';
            const country = data.countryName || '';
            setLocation(`${city}${country ? ', ' + country : ''}`);
          } catch (error) {
            console.error('Error getting location:', error);
            setLocation('Location unavailable');
          } finally {
            setLocationLoading(false);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          setLocation('Location denied');
          setLocationLoading(false);
        },
        { timeout: 10000, enableHighAccuracy: true }
      );
    } else {
      setLocation('Geolocation not supported');
      setLocationLoading(false);
    }
  };

  // Validate password strength
  const validatePassword = (pwd) => {
    const strength = {
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /\d/.test(pwd),
      symbol: /[!@#$%^&*(),.?":{}|<>]/.test(pwd)
    };
    setPasswordStrength(strength);
    return Object.values(strength).every(Boolean);
  };

  // Calculate age from birth date
  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  // Handle rate limiting
  const handleFailedAttempt = () => {
    const newCount = attemptCount + 1;
    setAttemptCount(newCount);

    const now = Date.now();
    const attemptData = {
      count: newCount,
      lastAttempt: now
    };

    // Block after 5 failed attempts
    if (newCount >= 5) {
      attemptData.blockedUntil = now + (15 * 60 * 1000); // 15 minutes
      setIsBlocked(true);
      setBlockTimeRemaining(15 * 60);
      setMessage('Too many failed attempts. Please try again in 15 minutes.');
    }

    localStorage.setItem('loginAttempts', JSON.stringify(attemptData));
  };

  // Reset rate limiting on successful login
  const resetRateLimit = () => {
    localStorage.removeItem('loginAttempts');
    setAttemptCount(0);
    setIsBlocked(false);
  };

  // Create profile and subscription after successful signup
  const createUserProfile = async (userId, userData) => {
    try {
      console.log('Creating profile for user:', userId, userData);
      
      // Use the safe database function that handles all signup creation
      const { data, error } = await supabase
        .rpc('safe_create_user_profile', {
          user_uuid: userId,
          user_email: userData.email,
          user_age: userData.age,
          user_location: userData.location || 'Unknown'
        });

      console.log('RPC response - data:', data, 'error:', error);

      if (error) {
        console.error('Profile creation error details:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        console.error('Error details:', error.details);
        throw new Error(`Database function error: ${error.message} (Code: ${error.code})`);
      }

      if (data && data.length > 0 && !data[0].success) {
        console.error('Profile creation failed:', data[0].message);
        throw new Error(data[0].message);
      }

      console.log('Profile creation successful');
      return true;
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    
    if (isBlocked) {
      setMessage(`Too many attempts. Try again in ${Math.ceil(blockTimeRemaining / 60)} minutes.`);
      return;
    }
    
    if (loading) return;
    setLoading(true);
    setMessage('');
    
    try {
      if (isLogin) {
        // Login validation
        if (!email || !password) {
          setMessage('Please fill in all fields.');
          setLoading(false);
          return;
        }

        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password: password,
        });
        
        if (error) {
          handleFailedAttempt();
          setMessage('Login failed: ' + error.message);
        } else {
          resetRateLimit();
          setMessage('Login successful!');
        }
      } else {
        // Signup validation
        if (!email || !password || !confirmPassword || !birthDate) {
          setMessage('Please fill in all fields.');
          setLoading(false);
          return;
        }

        // Age verification
        const age = calculateAge(birthDate);
        if (age < 18) {
          setMessage('You must be 18 or older to use Flirteen.');
          setLoading(false);
          return;
        }

        // Password strength validation
        if (!validatePassword(password)) {
          setMessage('Password does not meet security requirements.');
          setLoading(false);
          return;
        }

        // Password confirmation
        if (password !== confirmPassword) {
          setMessage('Passwords do not match.');
          setLoading(false);
          return;
        }

        // Step 1: Create auth user
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: email.trim(),
          password: password
        });
        
        if (authError) {
          setMessage('Signup failed: ' + authError.message);
          setLoading(false);
          return;
        }

        // Step 2: Create user profile after successful auth signup
        if (authData.user && authData.user.id) {
          try {
            await createUserProfile(authData.user.id, {
              email: email.trim(),  // ADD THIS LINE
              age: age,
              location: location || 'Unknown'
            });
            setMessage('Signup successful! Please check your email to verify your account.');
          } catch (profileError) {
            setMessage('Account created but profile setup incomplete. Please contact support if you have issues.');
            console.error('Profile creation failed:', profileError);
          }
        } else {
          setMessage('Signup successful! Please check your email to verify your account.');
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      setMessage('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
  e.preventDefault();
  
  if (!resetEmail) {
    setResetMessage('Please enter your email address.');
    return;
  }
  
  setResetLoading(true);
  setResetMessage('');
  
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    
    if (error) {
      setResetMessage('Error: ' + error.message);
    } else {
      setResetMessage('Password reset email sent! Check your inbox.');
    }
  } catch (error) {
    setResetMessage('Error sending reset email.');
  } finally {
    setResetLoading(false);
  }
};
  const isPasswordStrong = Object.values(passwordStrength).every(Boolean);

  return (
    <div className="page-container">
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
  <img 
    src="/flirteen-logo.png" 
    alt="Flirteen Logo" 
    style={{ 
      height: '120px', 
      marginBottom: '15px',
      filter: 'brightness(1.2) contrast(1.1)',
      maxWidth: '300px'
    }} 
  />
  <h2 style={{ 
    fontSize: '28px', 
    fontWeight: '600', 
    margin: 0,
    background: 'linear-gradient(45deg, #00E9FF, #E0005B)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  }}>
    Welcome Back
  </h2>
  
  {/* Back to Home Button */}
  {onShowHome && (
    <button
      onClick={onShowHome}
      style={{
        marginTop: '15px',
        padding: '8px 20px',
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'rgba(255, 255, 255, 0.8)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '20px',
        fontSize: '14px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        fontFamily: 'Poppins, sans-serif'
      }}
      onMouseOver={(e) => {
        e.target.style.background = 'rgba(255, 255, 255, 0.2)';
        e.target.style.borderColor = 'rgba(255, 255, 255, 0.4)';
      }}
      onMouseOut={(e) => {
        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
        e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
      }}
    >
      ← Back to Home
    </button>
  )}
</div>
      
      {isBlocked && (
        <div style={{
          background: 'rgba(255, 107, 107, 0.2)',
          border: '1px solid rgba(255, 107, 107, 0.4)',
          borderRadius: '10px',
          padding: '15px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, color: '#ff6b6b', fontWeight: '600' }}>
            🔒 Account temporarily locked
          </p>
          <p style={{ margin: '5px 0 0 0', color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
            Try again in {Math.floor(blockTimeRemaining / 60)}:{(blockTimeRemaining % 60).toString().padStart(2, '0')}
          </p>
        </div>
      )}
      
      <form onSubmit={handleAuth}>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="custom-input"
          required
          disabled={isBlocked}
        />
        
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (!isLogin) validatePassword(e.target.value);
          }}
          className="custom-input"
          required
          disabled={isBlocked}
        />

        {/* Password strength indicator for signup */}
        {!isLogin && password && (
          <div style={{
            marginTop: '10px',
            padding: '12px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: '600', color: '#00E9FF' }}>
              Password Requirements:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '11px' }}>
              <span style={{ color: passwordStrength.length ? '#51cf66' : '#ff6b6b' }}>
                {passwordStrength.length ? '✓' : '✗'} 8+ characters
              </span>
              <span style={{ color: passwordStrength.uppercase ? '#51cf66' : '#ff6b6b' }}>
                {passwordStrength.uppercase ? '✓' : '✗'} Uppercase letter
              </span>
              <span style={{ color: passwordStrength.lowercase ? '#51cf66' : '#ff6b6b' }}>
                {passwordStrength.lowercase ? '✓' : '✗'} Lowercase letter
              </span>
              <span style={{ color: passwordStrength.number ? '#51cf66' : '#ff6b6b' }}>
                {passwordStrength.number ? '✓' : '✗'} Number
              </span>
              <span style={{ color: passwordStrength.symbol ? '#51cf66' : '#ff6b6b', gridColumn: 'span 2' }}>
                {passwordStrength.symbol ? '✓' : '✗'} Special character (!@#$%^&*)
              </span>
            </div>
          </div>
        )}
        
        {/* Additional signup fields */}
        {!isLogin && (
          <>
            <input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="custom-input"
              required
              disabled={isBlocked}
            />
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontSize: '14px', 
                color: 'rgba(255, 255, 255, 0.8)' 
              }}>
                Birth Date (You must be 18+):
              </label>
              <input
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                className="custom-input"
                required
                disabled={isBlocked}
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
              />
            </div>

            {/* Location display */}
            <div style={{
              padding: '12px',
              background: 'rgba(0, 233, 255, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(0, 233, 255, 0.3)',
              marginBottom: '15px'
            }}>
              <p style={{ margin: '0 0 5px 0', fontSize: '12px', fontWeight: '600', color: '#00E9FF' }}>
                📍 Location:
              </p>
              <p style={{ margin: 0, fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>
                {locationLoading ? 'Getting your location...' : location || 'Unknown'}
              </p>
              {location === 'Location denied' && (
                <button
                  type="button"
                  onClick={getCurrentLocation}
                  style={{
                    marginTop: '5px',
                    padding: '4px 8px',
                    background: 'rgba(0, 233, 255, 0.2)',
                    border: '1px solid rgba(0, 233, 255, 0.4)',
                    borderRadius: '4px',
                    color: '#00E9FF',
                    fontSize: '11px',
                    cursor: 'pointer'
                  }}
                >
                  Try Again
                </button>
              )}
            </div>
          </>
        )}
        
        <button 
          type="submit" 
          className="custom-button"
          disabled={isBlocked || loading || (!isLogin && !isPasswordStrong)}
          style={{
            opacity: (isBlocked || loading || (!isLogin && !isPasswordStrong)) ? 0.6 : 1,
            cursor: (isBlocked || loading || (!isLogin && !isPasswordStrong)) ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Processing...' : (isLogin ? 'Login' : 'Sign Up')}
        </button>
      </form>
      
      {/* Rate limiting indicator */}
      {!isBlocked && attemptCount > 0 && attemptCount < 5 && (
        <p style={{ 
          marginTop: '10px', 
          textAlign: 'center',
          color: '#ffd43b',
          fontSize: '12px'
        }}>
          ⚠️ {attemptCount}/5 login attempts used
        </p>
      )}
      {isLogin && (
  <button
    type="button"
    onClick={() => setShowForgotPassword(true)}
    style={{
      background: 'none',
      border: 'none',
      color: '#00E9FF',
      textDecoration: 'underline',
      cursor: 'pointer',
      fontSize: '14px',
      marginTop: '10px'
    }}
  >
    Forgot Password?
  </button>
)}
{/* Password Reset Modal */}
{showForgotPassword && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  }}>
    <div style={{
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      borderRadius: '20px',
      padding: '30px',
      maxWidth: '400px',
      width: '90%',
      border: '2px solid #00E9FF',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)'
    }}>
      <h3 style={{ 
        color: '#00E9FF', 
        marginBottom: '20px',
        fontSize: '20px',
        textAlign: 'center'
      }}>
        Reset Password
      </h3>
      
      <form onSubmit={handlePasswordReset}>
        <input
          type="email"
          placeholder="Enter your email"
          value={resetEmail}
          onChange={(e) => setResetEmail(e.target.value)}
          className="custom-input"
          required
          disabled={resetLoading}
        />
        
        <button 
          type="submit" 
          className="custom-button"
          disabled={resetLoading}
          style={{
            opacity: resetLoading ? 0.6 : 1,
            marginTop: '15px'
          }}
        >
          {resetLoading ? 'Sending...' : 'Send Reset Email'}
        </button>
      </form>
      
      <button
        onClick={() => {
          setShowForgotPassword(false);
          setResetEmail('');
          setResetMessage('');
        }}
        style={{
          background: 'rgba(255, 255, 255, 0.2)',
          color: 'white',
          border: 'none',
          padding: '10px 20px',
          borderRadius: '10px',
          fontSize: '14px',
          cursor: 'pointer',
          marginTop: '10px',
          width: '100%'
        }}
      >
        Cancel
      </button>
      
      {resetMessage && (
        <p style={{ 
          marginTop: '15px', 
          textAlign: 'center',
          color: resetMessage.includes('sent') ? '#51cf66' : '#ff6b6b',
          fontWeight: '500',
          fontSize: '14px'
        }}>
          {resetMessage}
        </p>
      )}
    </div>
  </div>
)}
      <button 
        onClick={() => {
          setIsLogin(!isLogin);
          setMessage('');
          setPassword('');
          setConfirmPassword('');
          setBirthDate('');
        }}
        className="custom-button"
        style={{ background: 'rgba(255, 255, 255, 0.2)', marginTop: '15px' }}
        disabled={isBlocked}
      >
        Switch to {isLogin ? 'Sign Up' : 'Login'}
      </button>
      
      {message && (
        <p style={{ 
          marginTop: '15px', 
          textAlign: 'center',
          color: message.includes('successful') ? '#00E9FF' : '#E0005B',
          fontWeight: '500'
        }}>
          {message}
        </p>
      )}
    </div>
  );
}

export default Login;